import type * as GeoJSON from 'geojson'
import { feature, flattenEach } from '@turf/turf'
/**
 * Restriction Layer Helpers
 *
 * Utilities for creating Mapbox GL JS expressions for restriction polygon layers
 */

import { TFunction } from 'i18next'
import { translateRdppfZoneName } from '../../../../utils'

// Color palette for different restriction types
const RESTRICTION_TYPE_COLORS: Record<string, string> = {
  default: '#9333ea',
  heritage: '#dc2626',
  environmental: '#16a34a',
  zoning: '#0284c7',
  infrastructure: '#ea580c',
  conservation: '#65a30d',
}

export function isRestrictionLayer(layerId: string): boolean {
  return layerId.startsWith('restrictions-')
}

export function getRestrictionTypeFromLayerId(layerId: string): string {
  return layerId.replace('restrictions-', '')
}

export function getRestrictionColor(
  _rdppfTypeId: string,
  typeName?: string
): string {
  // Map based on type name patterns (case-insensitive)
  const lowerTypeName = typeName?.toLowerCase() || ''

  // Heritage/Cultural patterns
  if (
    lowerTypeName.includes('heritage') ||
    lowerTypeName.includes('monument') ||
    lowerTypeName.includes('historic') ||
    lowerTypeName.includes('cultural')
  ) {
    return RESTRICTION_TYPE_COLORS['heritage']
  }

  // Environmental patterns
  if (
    lowerTypeName.includes('environmental') ||
    lowerTypeName.includes('nature') ||
    lowerTypeName.includes('forest') ||
    lowerTypeName.includes('water') ||
    lowerTypeName.includes('protection')
  ) {
    return RESTRICTION_TYPE_COLORS['environmental']
  }

  // Conservation patterns
  if (
    lowerTypeName.includes('conservation') ||
    lowerTypeName.includes('landscape') ||
    lowerTypeName.includes('biotope')
  ) {
    return RESTRICTION_TYPE_COLORS['conservation']
  }

  // Zoning patterns
  if (
    lowerTypeName.includes('zone') ||
    lowerTypeName.includes('zoning') ||
    lowerTypeName.includes('plan') ||
    lowerTypeName.includes('land use')
  ) {
    return RESTRICTION_TYPE_COLORS['zoning']
  }

  // Infrastructure patterns
  if (
    lowerTypeName.includes('infrastructure') ||
    lowerTypeName.includes('road') ||
    lowerTypeName.includes('railway') ||
    lowerTypeName.includes('utility') ||
    lowerTypeName.includes('servitude') ||
    lowerTypeName.includes('easement')
  ) {
    return RESTRICTION_TYPE_COLORS['infrastructure']
  }

  // Default color for unknown types
  return RESTRICTION_TYPE_COLORS['default']
}

export function createRestrictionsGeoJSON(
  restrictions: any[]
): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = []
  for (const r of restrictions) {
    if (!r.geometry) continue
    flattenEach(feature(r.geometry), (part) => {
      features.push({
        ...part,
        id: features.length,
        properties: {
          id: String(r.id),
          name: r.name,
          type: r.type,
          rdppfTypeId: r.rdppfTypeId,
          coveragePerc: r.coveragePerc,
          instanceNumber: r.instanceNumber,
        },
      })
    })
  }
  return { type: 'FeatureCollection', features }
}

/**
 * Format restriction tooltip HTML
 * @param properties - Feature properties from GeoJSON
 * @param t - Optional translation function for zone names
 */
export function formatRestrictionTooltip(
  properties: any,
  t?: TFunction
): string {
  // coveragePerc is always in decimal format (0-1), so multiply by 100 to get percentage
  const coveragePercent =
    properties.coveragePerc != null &&
    typeof properties.coveragePerc === 'number'
      ? (properties.coveragePerc * 100).toFixed(1)
      : 'N/A'

  const displayName = t
    ? translateRdppfZoneName(properties.name, t)
    : properties.name
  const displayType = t
    ? translateRdppfZoneName(properties.type, t)
    : properties.type

  return `
    <strong>${displayName}</strong><br/>
    Type: ${displayType}<br/>
    Coverage: ${coveragePercent}${coveragePercent !== 'N/A' ? '%' : ''}
  `
}

// Get unique RDPPF types from restrictions data
export function getUniqueRdppfTypes(
  restrictions: any[]
): Array<{ id: string; name: string }> {
  const typesMap = new Map()
  restrictions.forEach((r) => {
    if (!typesMap.has(r.rdppfTypeId)) {
      typesMap.set(r.rdppfTypeId, { id: r.rdppfTypeId, name: r.type })
    }
  })
  return Array.from(typesMap.values())
}

// Get restrictions for a specific type
export function getRestrictionsByType(
  restrictions: any[],
  typeId: string
): any[] {
  return restrictions.filter((r) => r.rdppfTypeId === typeId)
}
